import { useInfiniteQuery } from "@tanstack/react-query";
import { api } from "@/lib/backend";
import type { TypedMessage } from "@/lib/query/conversation/conversation.types";
import { MESSAGE_QUERY_KEY } from "./message.query.key";

const ITEMS_PER_PAGE = 20;

export function useInfiniteMessages(conversationId: string | undefined) {
  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, refetch } =
    useInfiniteQuery({
      queryKey: [MESSAGE_QUERY_KEY, "infinite", conversationId],
      queryFn: async (args) => {
        const res = await api.messageCrud.getAll({
          filter: {
            conversationId: { operator: "equals", value: conversationId! },
          },
          sort: { field: "createdAt", order: "desc" },
          include: {
            recipients: { select: true },
          },
          pagination: {
            take: ITEMS_PER_PAGE,
            page: args.pageParam + 1,
          },
        });
        return res.data;
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        const totalItems = lastPage.count;
        const currentItems = (lastPageParam + 1) * ITEMS_PER_PAGE;

        if (currentItems >= totalItems) {
          return undefined;
        }

        return lastPageParam + 1;
      },
      enabled: !!conversationId,
    });

  // Flatten pages and reverse to show oldest first (for display)
  const messages = (data?.pages.flatMap((page) => page.items).reverse() ?? []) as TypedMessage[];

  const loadMore = async (ev: CustomEvent) => {
    await fetchNextPage();
    if (ev.target) {
      (ev.target as HTMLIonInfiniteScrollElement).complete();
    }
  };

  return {
    messages,
    loadMore,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    refetch,
  };
}
